import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface AuthorBook {
  id: string;
  title: string;
  cover_url?: string;
  published_year?: number;
  synopsis?: string;
  rating_count?: number;
  average_rating?: number;
  isExternal?: boolean;
  open_library_key?: string;
  series_name?: string;
  series_position?: number;
  detected_series_name?: string;
  detected_series_position?: number;
}

interface SeriesGroup {
  series_name: string;
  books: AuthorBook[];
  book_count: number;
  first_published: number;
}

interface AuthorDetails {
  authorName: string;
  bio?: string;
  bioSource?: string;
  imageUrl?: string;
  birthYear?: number;
  nationality?: string;
  awards?: string;
  authorAverageRating?: string;
  mostPopularBooks: AuthorBook[];
  highestRatedBooks: AuthorBook[];
  seriesGroups: SeriesGroup[];
  localBooks: AuthorBook[];
  externalBooks: AuthorBook[];
  totalLocalBooks: number;
  totalBooks: number;
  followerCount: number;
  readerCount: number;
}

export function useAuthorDetails(authorName: string | null, limit: number = 20, offset: number = 0) {
  return useQuery({
    queryKey: ['author-details', authorName, limit, offset],
    queryFn: async (): Promise<AuthorDetails> => {
      if (!authorName) throw new Error('Author name required');

      const { data, error } = await supabase.functions.invoke('get-author-details', {
        body: { authorName, limit, offset }
      });

      if (error) throw error;
      return data;
    },
    enabled: !!authorName,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
